import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { Controller } from 'react-hook-form'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import FormInput from '@/elements/FormInput'
import { LockupWhite } from '@/assets'
import toast from '@/utils/toast'
import { setPassword } from '@/api/auth'
import useSetPasswordForm from '@/hooks/forms/useSetPasswordForm'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useTheme } from '@/utils/theme'
import { Lock } from 'lucide-react'

function SetPassword() {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoints()
  const { theme } = useTheme()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('t') ?? ''
  let email = ''
  try { email = atob(token) } catch { /* invalid token */ }

  const form = useSetPasswordForm()

  const setPasswordMutation = useMutation({
    mutationFn: setPassword,
    onSuccess: () => {
      toast.success('Password set successfully')
      setTimeout(() => {
        navigate('/')
      }, 2000)
    },
    onError: () => {
      toast.error('Failed to set password')
    },
  })

  return (
    <Flex
      component='main'
      align='center'
      justify='center'
      gap={!isMobile ? 6 : 4}
      py={!isMobile ? 7 : 5}
      px={4}
      className='auth'
      style={{ minHeight: '100svh' }}
    >
      <LockupWhite style={{ width: 162, maxWidth: '80%' }} />

      <Flex
        align='center'
        gap={2}
        grow
        style={{
          width: !isMobile ? 306 : '100%',
          maxWidth: '100%'
        }}
      >
        <Flex align='center' pb={4}>
          <Text variant='h1' style={{ fontSize: '2rem' }} align='center'>
            Set Password
          </Text>
          <Text variant='textblock' color='black-light-80' align='center'>
            Choose a password for your account.
          </Text>
        </Flex>

        <form
          onSubmit={form.handleSubmit(data =>
            setPasswordMutation.mutate({
              email,
              password: data.newPassword,
            })
          )}
        >
          <Flex gap={3} wfull>
            <Controller
              control={form.control}
              name='newPassword'
              render={({ field }) => (
                <FormInput
                  {...field}
                  type='password'
                  label='New Password'
                  placeholder='Enter your new password'
                  error={form.formState.errors.newPassword?.message}
                  dirty={!!form.formState.dirtyFields.newPassword}
                  style={{ width: '100%' }}
                  Icon={Lock}
                />
              )}
            />
            <Controller
              control={form.control}
              name='passwordConfirm'
              render={({ field }) => (
                <FormInput
                  {...field}
                  type='password'
                  label='Confirm Password'
                  placeholder='Re-enter your new password'
                  error={form.formState.errors.passwordConfirm?.message}
                  dirty={!!form.formState.dirtyFields.passwordConfirm}
                  style={{ width: '100%' }}
                  Icon={Lock}
                />
              )}
            />
            <Flex className='form-controls' pt={2}>
              <Button
                submit
                variant='accent'
                disabled={setPasswordMutation.isPending}
                fullWidth
              >
                {setPasswordMutation.isPending ? 'Setting password…' : 'Set Password'}
              </Button>
            </Flex>
          </Flex>
        </form>

        <Button
          fullWidth
          variant={theme === 'dark' ? 'secondary' : 'primary'}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Flex>
    </Flex>
  )
}

export default SetPassword
